import { useEffect, useState, useRef } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import "../../Projects/CreateProjectMspa/Services/Questionnaire/Survey.css";
import { SurveyResponseType } from "../types/SurveyResponseType";
import FormTextField from "@components/Forms/FormTextField";
import RequiredReactSelect from "@components/FormsV2/RequiredReactSelect/RequiredReactSelect";
import { Section } from "../../ProjectSurveysV2/types";
import axios from "axios";

const Survey = ({
  questionnaire,
  questions,
  setSurveyResponses,
  surveyResponses,
  handleSurveySubmit,
  showRecommendations,
  isFromSurveyPage = false,
  currentSurvey,
  setRecalculateClicked,
  isNewSurvey,
  calculateClicked,
  setCalculateClicked,
  revertClicked,
  originalSurveyResponses,
  isTakeSharedSurvey = false,
  name = "",
  setName,
  showNoAccessError = false,
  headers = undefined as any,
  evaluateQuestionnaire = undefined as any,
}): JSX.Element => {
  const [questionsState, setQuestionsState] = useState(questions);
  const [sections, setSections] = useState<Section[]>([]);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const newSectionRef = useRef<HTMLDivElement | null>(null);
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;

  if (
    !isNewSurvey ||
    (currentSurvey && currentSurvey?.attributes?.status === "shared")
  ) {
    isNewSurvey =
      surveyResponses.filter((sr) => sr["survey-response-id"]).length === 0;
  }

  useEffect(() => {
    if (!isNewSurvey) {
      let surveyResponsesToRender = revertClicked
        ? originalSurveyResponses
        : surveyResponses;
      surveyResponsesToRender = surveyResponsesToRender.map((sr) => {
        questions.forEach((q) => {
          if (sr["question-id"] == q.id) {
            sr = {
              ...sr,
              settings: q.attributes ? q.attributes.settings : q.settings,
            };
          }
        });
        return sr;
      });
      setSurveyResponses(surveyResponsesToRender);
      const responses = surveyResponsesToRender.map((question) => {
        const questionResource = questions.find(
          (q) => Number(q.id) === Number(question["question-id"])
        );
        let selectOptions = question.attributes
          ? question.attributes["select-options"]
          : question["select-options"];

        if (selectOptions.length > 0) {
          let defaultValue = {
            label: "Select An Option",
            value: "",
          };

          selectOptions.forEach((option) => {
            if (option.default && !showRecommendations) {
              defaultValue = {
                value: option.value,
                label: option.key,
              };
            }
            if (question.answer !== "" && question.answer == option.value) {
              defaultValue = {
                value: option.value,
                label: option.key,
              };
            }
          });

          if (question.attributes) {
            question = {
              ...question,
              attributes: {
                ...question.attributes,
                id: question.id,
                answer: defaultValue,
              },
            };
            question = question.attributes;
          } else {
            question = { ...question, answer: defaultValue };
          }

          return {
            ...question,
            "questionnaire-section-id":
              questionResource?.attributes?.["questionnaire-section-id"],
          };
        }
        return {
          ...question,
          "questionnaire-section-id":
            questionResource?.attributes?.["questionnaire-section-id"],
        };
      });

      setQuestionsState(responses);

      (async () => {
        await evaluateSections(questionnaire.attributes.sections, responses);
      })();
    }
  }, [
    showRecommendations,
    calculateClicked,
    revertClicked,
    currentSurvey,
    originalSurveyResponses,
  ]);

  useEffect(() => {
    if (isNewSurvey) {
      const newQuestionsState = (questions || [])
        .filter((question) => question.attributes.active)
        // v2 keeps the questionnaire-wide order in `questionnaire-position`
        // (`position` is only the order within a section); v1 has it in
        // `position`.
        .sort(
          (a, b) =>
            (a.attributes["questionnaire-position"] ?? a.attributes.position) -
            (b.attributes["questionnaire-position"] ?? b.attributes.position)
        )
        .map((question) => {
          let selectOptions = question.attributes
            ? question.attributes["select-options"]
            : question["select-options"];
          if (selectOptions.length > 0) {
            let defaultValue = {
              label: "Select An Option",
              value: "",
            };
            selectOptions.forEach((option) => {
              if (option.default && !showRecommendations) {
                defaultValue = {
                  value: option.value,
                  label: option.key,
                };
              }
            });
            question = {
              ...question,
              attributes: {
                ...question.attributes,
                id: question.id,
                answer: defaultValue,
              },
            };
            question = question.attributes;
            return question;
          } else {
            question = {
              ...question,
              attributes: {
                ...question.attributes,
                id: question.id,
                answer: "",
              },
            };
            question = question.attributes;
            return question;
          }
        });
      setQuestionsState(newQuestionsState);
      setSections(questionnaire?.attributes?.sections || []);
      setSurveyResponses(
        newQuestionsState.map((question) => {
          return {
            question: question.question,
            "question-id": question.id,
            answer: question.answer.value ? question.answer.value : "",
          };
        })
      );
    }
  }, []);

  useEffect(() => {}, [showNoAccessError]);

  useEffect(() => {
    if (shouldScrollToBottom && newSectionRef.current) {
      newSectionRef.current.scrollIntoView({ behavior: "smooth" });
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, sections]);

  const customValidateReactSelect = () => {
    let isValid = true;
    const newQuestionState = questionsState.map((question) => {
      const section = sections.find(
        (s) => s.id === question["questionnaire-section-id"]
      );
      if (
        question["select-options"] &&
        question["select-options"].length > 0 &&
        question.answer.value == "" &&
        question.required &&
        section?.show
      ) {
        isValid = false;
        question = { ...question, showInvalid: true };
      } else {
        question = { ...question, showInvalid: false };
      }
      return question;
    });
    setQuestionsState(newQuestionState);
    return isValid;
  };

  const evaluateSections = async (sections, responses) => {
    //flips to true if at least one hidden section evaluates to true
    let showNewSection = false;

    const updated = await Promise.all(
      sections?.map(async (section) => {
        //only execute for sections that have an expression declared
        if (!section.expression) return section;

        const data = {
          data: {
            id: Number(questionnaire.id),
            type: "questionnaires" as "questionnaires",
            attributes: {
              responses:
                responses?.map((q) => {
                  return {
                    question_id: Number(q?.id || q?.["question-id"] || 0),
                    question: q.question || "",
                    answer: q?.answer?.label ? q?.answer?.value : q.answer,
                  };
                }) || [],
              equation: section.expression || "",
            },
          },
        };

        // In-app callers pass the generated v2 trigger. The public shared
        // survey page has no store or bearer token, so it still posts directly.
        const evaluation = evaluateQuestionnaire
          ? await evaluateQuestionnaire({
              id: String(questionnaire.id),
              body: data,
            }).unwrap()
          : (
              await axios({
                method: "POST",
                url: `${apiHost}/v2/questionnaires/${questionnaire.id}/evaluations`,
                data,
                headers,
              })
            ).data;

        const result = evaluation?.data?.attributes?.result;

        //If any section is currently not showing, but its expression evaluates to true, trigger early return
        if (!section.show && result) showNewSection = true;

        return {
          ...section,
          //@ts-ignore
          show: !!result,
        };
      }) || []
    );
    setSections(updated);

    // Do not proceed to calculate if a new section is shown
    if (showNewSection) setShouldScrollToBottom(true);

    return showNewSection;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = customValidateReactSelect();

    const showNewSection = await evaluateSections(sections, questionsState);
    if (showNewSection) return;

    if (showRecommendations && currentSurvey && !isNewSurvey && isValid) {
      setRecalculateClicked(true);
      handleSurveySubmit(e, false, true);
    } else if (isValid) {
      setCalculateClicked(true);
      handleSurveySubmit(e, false, false);
    }
  };

  const buildInputElement = (question) => {
    let response: SurveyResponseType = {
      question: "",
      answer: "",
      surveyResponseId: 0,
      questionId: 0,
    };
    surveyResponses.forEach((res) => {
      if (question.id == res["question-id"]) {
        response = res;
      }
    });
    if (question["select-options"] && question["select-options"].length > 0) {
      return (
        <Col key={question.id} xs={6}>
          <Form.Group controlId={question.id}>
            <RequiredReactSelect
              label={
                <span>
                  {isFromSurveyPage && question.required ? (
                    <span style={{ color: "#FD0300" }}>* </span>
                  ) : null}
                  <span>{question.question}</span>
                </span>
              }
              required={question.required}
              isInvalid={question.showInvalid}
              key={question.id || question["survey-response-id"]}
              value={question.answer}
              onChange={(e: any) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: e,
                        showInvalid: false,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: e?.value,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
              options={question["select-options"].map((option) => {
                return {
                  label: option.key,
                  value: option.value,
                };
              })}
            />
          </Form.Group>
        </Col>
      );
    } else if (
      question["value-type"] === "text" ||
      question["value-type"] === "email"
    ) {
      return (
        <Col key={question.id || question["survey-response-id"]} xs={6}>
          <Form.Group className="formGroup">
            <Form.Label>
              {isFromSurveyPage && question.required ? (
                <span style={{ color: "#FD0300" }}>* </span>
              ) : null}
              {question.question}
            </Form.Label>
            <Form.Control
              required={question.required}
              type={question["value-type"]}
              value={question.answer}
              onChange={(e) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: e.target.value,
                        showInvalid: false,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: e.target.value,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
            />
          </Form.Group>
        </Col>
      );
    } else if (question["value-type"] === "number") {
      return (
        <Col key={question.id || question["survey-response-id"]} xs={6}>
          <Form.Group className="formGroup">
            <Form.Label>
              {isFromSurveyPage && question.required ? (
                <span style={{ color: "#FD0300" }}>* </span>
              ) : null}
              {question.question}
            </Form.Label>
            <Form.Control
              required={question.required}
              type="number"
              value={question.answer}
              min={
                question.settings && question.settings.min
                  ? parseInt(question.settings.min)
                  : 0
              }
              max={
                question.settings && question.settings.max
                  ? parseInt(question.settings.max)
                  : undefined
              }
              step={question?.settings?.step || 1}
              onChange={(e) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: e.target.value,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: e.target.value,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
            />
          </Form.Group>
        </Col>
      );
    } else if (question["value-type"] === "checkbox") {
      return (
        <Col key={question.id || question["survey-response-id"]} xs={6}>
          <Form.Group controlId={question.id}>
            <Form.Check
              type="checkbox"
              value={question.answer}
              label={
                <span>
                  {isFromSurveyPage && question.required ? (
                    <span style={{ color: "#FD0300" }}>* </span>
                  ) : null}
                  {question.question}
                </span>
              }
              required={question.required}
              onChange={(e) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: e.target.checked,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: e.target.checked,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
            />
          </Form.Group>
        </Col>
      );
    } else if (question["value-type"] === "date") {
      return (
        <Col key={question.id || question["survey-response-id"]} xs={6}>
          <Form.Group controlId={question.id} className="formGroup">
            <Form.Label>
              {isFromSurveyPage && question.required ? (
                <span style={{ color: "#FD0300" }}>* </span>
              ) : null}
              {question.question}
            </Form.Label>
            <Form.Control
              required={question.required}
              value={question.answer}
              onChange={(date) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: date?.target.value,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: date?.target.value,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
              min={question.settings.min}
              max={question.settings.max}
              type="date"
            />
          </Form.Group>
        </Col>
      );
    } else if (question["value-type"] === "phone") {
      return (
        <Col key={question.id || question["survey-response-id"]} xs={6}>
          <Form.Group controlId={question.id} className="formGroup">
            <Form.Label>
              {isFromSurveyPage && question.required ? (
                <span style={{ color: "#FD0300" }}>* </span>
              ) : null}
              {question.question}
            </Form.Label>
            <input
              className="form-control"
              required={question.required}
              value={question.answer}
              placeholder="2125553456"
              pattern="^\+?(?:[0-9]●?){6,14}[0-9]$"
              type="tel"
              onChange={(e) => {
                setQuestionsState(
                  questionsState.map((ques) => {
                    if (
                      (isNewSurvey && ques.id == question.id) ||
                      (!isNewSurvey &&
                        ques["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...ques,
                        answer: e.target.value,
                        showInvalid: false,
                      };
                    } else {
                      return ques;
                    }
                  })
                );
                const updatedSurveyResponses = surveyResponses.map(
                  (response) => {
                    if (
                      (isNewSurvey && response["question-id"] == question.id) ||
                      (!isNewSurvey &&
                        response["survey-response-id"] ==
                          question["survey-response-id"])
                    ) {
                      return {
                        ...response,
                        answer: e.target.value,
                      };
                    }
                    return response;
                  }
                );
                setSurveyResponses(updatedSurveyResponses);
              }}
            />
          </Form.Group>
        </Col>
      );
    }
  };

  const showSections =
    sections?.length > 1 ||
    sections?.[0]?.name !== questionnaire?.name ||
    sections?.[0]?.name !== questionnaire?.attributes?.name;

  return (
    <>
      <Row className="mb-3">
        <Col xs={isFromSurveyPage ? 12 : 6}>
          {!isFromSurveyPage ? (
            <>
              <div
                style={{ fontSize: "20px", margin: "0 0 30px 0" }}
                className="accordianSubtitle"
              >
                Complete Survey to Continue. When finished, click “Calculate”.
              </div>
              <div style={{ marginBottom: "20px" }}>
                {questionnaire.attributes.introduction}
              </div>
            </>
          ) : null}

          <Form
            onSubmit={(e) => handleSubmit(e)}
            id={isFromSurveyPage ? "takeSurveyForm" : "surveyForm"}
          >
            {isTakeSharedSurvey && (
              <div className="questionnaireSection">
                <div className="sectionHeader">
                  <h4>Contact Details</h4>
                </div>
                <FormTextField
                  label="Your Name"
                  id="name"
                  placeholder="Enter Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={true}
                  readOnly={false}
                />
              </div>
            )}
            {showSections ? (
              sections
                ?.filter((section) => !section.expression || section.show)
                ?.map((section, i) => {
                  const isLastVisible =
                    sections.filter((s) => s.show).at(-1)?.id === section.id;
                  return (
                    <div
                      className="questionnaireSection"
                      key={i}
                      ref={
                        isLastVisible && newSectionRef ? newSectionRef : null
                      }
                    >
                      <div className="sectionHeader">
                        <h4>
                          {i === 0 &&
                          section?.name === questionnaire?.attributes?.name
                            ? "Responses"
                            : section?.name}
                        </h4>
                        {section?.introduction && (
                          <div className="sectionIntro">
                            {section.introduction}
                          </div>
                        )}
                      </div>
                      <Row className="mb-3">
                        {questionsState
                          .filter(
                            (q) =>
                              q?.["questionnaire-section-id"] === section.id
                          )
                          .map((question) => {
                            return buildInputElement(question);
                          })}
                      </Row>
                    </div>
                  );
                })
            ) : (
              <div className="questionnaireSection">
                <div className="sectionHeader">
                  <h4>Responses</h4>
                </div>
                <Row className="mb-3">
                  {(questionsState || []).map((question) => {
                    return buildInputElement(question);
                  })}
                </Row>
              </div>
            )}
            <Button
              style={{ marginRight: "10px" }}
              id="surveyCalcButton"
              className="seafoamBgButton"
              type="submit"
              disabled={showNoAccessError}
              form={isFromSurveyPage ? "takeSurveyForm" : "surveyForm"}
            >
              {sections.length > 1
                ? "Continue"
                : isTakeSharedSurvey
                ? "Submit"
                : "Calculate"}
            </Button>
          </Form>
        </Col>
      </Row>
    </>
  );
};

export default Survey;
